import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Sparkles, Wrench, ShieldCheck, Loader2 } from "lucide-react";

const TYPE_META = {
  feature: { label: "Novità", icon: Sparkles, cls: "bg-[#1B3B2B] text-[#FAF8F5]" },
  improvement: { label: "Miglioramento", icon: Wrench, cls: "bg-[#8C532B] text-[#FAF8F5]" },
  important_fix: { label: "Correzione importante", icon: ShieldCheck, cls: "bg-amber-600 text-white" },
};

const fmtIT = (d) => { if (!d) return "—"; const [y, m, dd] = String(d).slice(0, 10).split("-"); return (y && m && dd) ? `${dd}/${m}/${y}` : String(d); };

function NoteBody({ note }) {
  const meta = TYPE_META[note.type] || TYPE_META.feature;
  const Icon = meta.icon;
  return (
    <div className="space-y-3">
      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-mono-label ${meta.cls}`}>
        <Icon className="h-3.5 w-3.5" /> {meta.label}
      </span>
      <p className="text-sm text-[#5F6B64]">{note.summary}</p>
      {(note.bullets || []).length > 0 && (
        <ul className="list-disc pl-5 space-y-1 text-sm text-[#1B3B2B]">
          {note.bullets.map((b, i) => <li key={i} data-testid={`news-bullet-${i}`}>{b}</li>)}
        </ul>
      )}
    </div>
  );
}

export default function ReleaseNotesGate({ historyOpen, setHistoryOpen }) {
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/release-notes/unseen").then((r) => { if (alive) setQueue(r.data || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const current = queue[0];
  const ack = async () => {
    if (!current) return;
    setBusy(true);
    try { await api.post(`/release-notes/${current.id}/ack`); setQueue((q) => q.slice(1)); }
    catch { /* keep in queue; will retry next load */ }
    finally { setBusy(false); }
  };

  const loadHistory = useCallback(() => {
    api.get("/release-notes/history").then((r) => setHistory(r.data || [])).catch(() => setHistory([]));
  }, []);
  useEffect(() => { if (historyOpen) loadHistory(); }, [historyOpen, loadHistory]);

  return (
    <>
      {/* Auto-shown unseen published note. Not dismissible except via "Ho visto". */}
      <Dialog open={!!current} onOpenChange={() => { /* gated: only "Ho visto" closes */ }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="news-gate-dialog"
          onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()}>
          {current && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display" data-testid="news-gate-title">{current.title}</DialogTitle>
                <DialogDescription className="text-xs">Novità in Equiro{queue.length > 1 ? ` · ${queue.length} da vedere` : ""}</DialogDescription>
              </DialogHeader>
              <NoteBody note={current} />
              <DialogFooter>
                <Button onClick={ack} disabled={busy} data-testid="news-gate-ack" className="bg-[#1B3B2B] hover:bg-[#142E1F]">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Ho visto
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* History of published notes (discreet access). */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="news-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Novità di Equiro</DialogTitle>
            <DialogDescription className="text-xs">Tutti gli aggiornamenti pubblicati.</DialogDescription>
          </DialogHeader>
          {history === null ? (
            <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#1B3B2B]" /></div>
          ) : history.length === 0 ? (
            <p className="text-sm text-[#8FA396] py-6 text-center" data-testid="news-history-empty">Nessuna novità per ora.</p>
          ) : (
            <div className="space-y-4">
              {history.map((n) => (
                <div key={n.id} data-testid={`news-history-item-${n.id}`} className="rounded-xl border border-[#E5DEC9] bg-white p-4">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="font-display font-semibold text-[#1B3B2B]">{n.title}</p>
                    <span className="text-[11px] text-[#9AA39C] shrink-0">{fmtIT(n.published_at)}</span>
                  </div>
                  <NoteBody note={n} />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)} data-testid="news-history-close">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
